import { useSearchParams } from 'react-router-dom'
import { clone, throttle, uniqBy } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { ContactCell } from 'app/components/shared/ContactCell'
import { ItemsTitle } from 'app/components/shared/styled/ItemsTitle'
import { AnchorCell } from 'app/components/table/AnchorCell'
import TableLayout from 'app/components/table/TableLayout'
import { formatDate } from 'app/date.utils'
import type { ContactJson } from 'app/types/entities'
import { ConversionType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import type { ConversionLogWithContact } from 'types/analytics'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { useConversionLogStore } from 'app/stores/StoreContext'
import { currentUrlWithParam } from 'lib/currentUrlWithParam'

type Props = {
  title: string
  conversionType: ConversionType
  fromDate: string
  toDate: string
  locationIds: string[]
  status?: `sent` | `failed`
  onClose?: () => void
}

const ConversionContactTable = observer(function ConversionContactTable({
  title,
  conversionType,
  fromDate,
  toDate,
  locationIds,
  status,
  onClose,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const conversionLogStore = useConversionLogStore()

  const [page, setPage] = useState(0)
  const [items, setItems] = useState<ConversionLogWithContact[]>([])
  const [total, setTotal] = useState(0)
  const [haveMore, setHaveMore] = useState(true)
  const isLoadingRef = useRef(false)

  // Contact drawer state
  const [focusedContact, setFocusedContact] = useState<ContactJson | undefined>()
  const [openContactDrawer, setOpenContactDrawer] = useState(false)

  const throttledSetPage = throttle(
    () => {
      if (isLoadingRef.current) return
      isLoadingRef.current = true
      setPage((p) => p + 1)
    },
    1000,
    { leading: true, trailing: false },
  )

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event) => {
    const element = event.target as HTMLDivElement
    if (
      !isLoadingRef.current &&
      haveMore &&
      !conversionLogStore.currentLoading &&
      element.clientHeight + element.scrollTop >= element.scrollHeight - 400
    ) {
      throttledSetPage()
    }
  }

  // Stable reference for locationIds dependency
  const locationIdsKey = locationIds.join(`,`)

  // Fetch logs when filters change
  useEffect(() => {
    if (conversionLogStore.currentLoading) return

    const terms: string[] = [`conversionType:${conversionType}`, `conversionDateTime:[${fromDate} TO ${toDate}]`]
    if (status) terms.push(`status:${status}`)
    if (locationIds.length) terms.push(`locationId:(${locationIds.join(` OR `)})`)

    conversionLogStore
      .fetchList({
        search: terms.join(` AND `),
        pagination: { page, limit: 100 },
        sort: { field: `createdDate`, direction: `DESC` as const },
      })
      .then((result) => {
        const hits = (result.data as any)?.conversionLogs?.hits ?? []
        const resultTotal = (result.data as any)?.conversionLogs?.total ?? 0
        setHaveMore(!!hits.length)
        setTotal(resultTotal)
        setItems((prev) => {
          if (page === 0) return hits
          return clone(uniqBy((prev || []).concat(hits), `id`))
        })
        isLoadingRef.current = false
      })
    isLoadingRef.current = true
  }, [page, conversionType, fromDate, toDate, locationIdsKey, status])

  // Contact drawer handlers with deep linking
  const contactHandler = (contactId: string) => {
    if (focusedContact?.id === contactId) return
    setSearchParams((prev) => {
      prev.set(`details`, conversionType)
      prev.set(`drawer`, contactId)
      return prev
    })
    setFocusedContact(undefined)
    gqlFetchOne<ContactJson>(
      `contact`,
      contactId,
      `id name cellPhone locationId orgId workspaceId leadStatus patientStatus`,
    ).then((contact) => {
      if (contact?.id) {
        setFocusedContact(contact)
      } else {
        closeDrawerHandler()
      }
    })
    setOpenContactDrawer(true)
  }

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    setSearchParams((prev) => {
      prev.set(`details`, conversionType)
      prev.delete(`drawer`)
      return prev
    })
  }

  // Watch for drawer URL param and open drawer if present
  const drawerParam = searchParams.get(`drawer`)
  useEffect(() => {
    if (!drawerParam || openContactDrawer || focusedContact?.id === drawerParam) return
    // Defer to allow component state to initialize on page refresh
    const timeoutId = setTimeout(() => {
      contactHandler(drawerParam)
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [drawerParam])

  const dateCell = (field: keyof ConversionLogWithContact, format = `MMM DD, YYYY`) => ({
    cell: ({ row }: { row: { original: ConversionLogWithContact } }) => {
      const val = row.original[field]
      return <div className="text-sm">{val ? formatDate({ value: new Date(val as string), format }) : `-`}</div>
    },
  })

  const columns = useMemo(
    () => [
      {
        id: `contactName`,
        header: `Contact`,
        accessorKey: `contactName`,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row?: ConversionLogWithContact) => (
            <AnchorCell
              href={currentUrlWithParam(`drawer`, row?.contactId!)}
              tooltipMessage={row?.contactName ?? ``}
              clickHandler={() => contactHandler(row?.contactId!)}
            >
              <ContactCell contact={{ name: row?.contactName, id: row?.contactId } as ContactJson} />
            </AnchorCell>
          ),
        },
      },
      {
        id: `becameLeadDate`,
        header: `Became Lead`,
        accessorKey: `becameLeadDate`,
        meta: { defaultVisible: true },
        ...dateCell(`becameLeadDate`),
      },
      {
        id: `firstAppointmentMadeDate`,
        header: `Appt Scheduled`,
        accessorKey: `firstAppointmentMadeDate`,
        meta: { defaultVisible: true },
        ...dateCell(`firstAppointmentMadeDate`),
      },
      {
        id: `becamePatientDate`,
        header: `Became Patient`,
        accessorKey: `becamePatientDate`,
        meta: { defaultVisible: true },
        ...dateCell(`becamePatientDate`),
      },
      {
        id: `conversionDateTime`,
        header: `Event Date`,
        accessorKey: `conversionDateTime`,
        meta: { defaultVisible: true },
        ...dateCell(`conversionDateTime`, `MMM DD, YYYY HH:mm`),
      },
      {
        id: `createdDate`,
        header: `Sent Date`,
        accessorKey: `createdDate`,
        meta: { defaultVisible: true },
        ...dateCell(`createdDate`, `MMM DD, YYYY HH:mm`),
      },
      {
        id: `status`,
        header: `Status`,
        accessorKey: `status`,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ConversionLogWithContact } }) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              row.original.status === `sent` ? `bg-green-100 text-green-800` : `bg-red-100 text-red-800`
            }`}
          >
            {row.original.status === `sent` ? `succeeded` : row.original.status}
          </span>
        ),
      },
      {
        id: `conversionValue`,
        header: `Value`,
        accessorKey: `conversionValue`,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ConversionLogWithContact } }) => (
          <div className="text-sm">
            {row.original.conversionValue ? `$${row.original.conversionValue.toFixed(2)}` : `-`}
          </div>
        ),
      },
      {
        id: `googleAdsAccountId`,
        header: `Account`,
        accessorKey: `googleAdsAccountId`,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ConversionLogWithContact } }) => (
          <div className="font-mono text-sm">{row.original.googleAdsAccountId}</div>
        ),
      },
      {
        id: `gclid`,
        header: `GCLID`,
        accessorKey: `gclid`,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ConversionLogWithContact } }) => (
          <div className="max-w-[200px] truncate font-mono text-xs" title={row.original.gclid || ``}>
            {row.original.gclid || `-`}
          </div>
        ),
      },
      {
        id: `errorMessage`,
        header: `Error`,
        accessorKey: `errorMessage`,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ConversionLogWithContact } }) => (
          <div className="max-w-xs truncate text-sm text-red-600" title={row.original.errorMessage || ``}>
            {row.original.errorMessage || `-`}
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden bg-surface px-5">
      <div className="flex h-full w-full">
        <div className="flex h-full w-full flex-col overflow-hidden">
          <ItemsTitle
            title={title}
            items={total}
            onClose={() => {
              onClose?.()
              closeModal()
            }}
          />
          {/* Table */}
          <div className="flex-1 overflow-auto" onScroll={scrollHandler}>
            <TableLayout data={{ hits: items, total }} columns={columns} hideControls />
          </div>
        </div>
        {/* Contact Drawer */}
        <ContactDrawer
          openDrawer={openContactDrawer}
          contact={focusedContact}
          onClose={closeDrawerHandler}
          onChange={(updated) => setFocusedContact(updated)}
        />
      </div>
    </div>
  )
})

export default ConversionContactTable
